import type { UsageGrant } from '../models/useUsage'
// ⚠ The `.ts` extension is deliberate, for the reason `usageAxis.ts` states on its own copy of
// this line: `grants.test.ts` runs under `node --experimental-strip-types` and node resolves no
// extension for you. `allowImportingTsExtensions` is on, and Vite resolves it unchanged.
import { formatBytes, formatCents } from './general.ts'

// A grant the customer should be told about, with the axis it enlarges and when it ends.
export type Boost = {
  axis: string
  amount: number // in the axis's own unit: bytes for bandwidth, cents for compute
  expiresAt: string | null
  kind: string
}

// The kinds a customer is told about. A promotion or a service credit makes THIS period bigger
// than the plan they bought, which is the thing that needs explaining.
//
// ⚠ `plan_allowance` is deliberately absent: it IS the plan, so announcing it as a boost would
// tell every customer their allowance is temporarily larger than their allowance. `purchased`
// is money the customer bought and belongs with the balance, not here.
const ANNOUNCED = new Set(['promotion', 'compensation'])

// liveBoosts picks the grants worth a notice.
//
// ⚠⚠ IT IS SCOPED TO THE CURRENT PERIOD AND NOTHING ELSE. `get_org_credit` answers "what does this
// org hold NOW" — it takes no period — so rendering it beside a PAST month would tell a customer
// that a boost they have today applied to a bill that was already issued. The usage page has a
// six-month picker, so this is reachable in one click and would be wrong for five of the six.
//
// ⚠ `remaining` and `expiresAt` are read TOGETHER. A grant that is spent is as gone as one that
// has lapsed, and a notice about either is a promise the meters below it will not keep.
export const liveBoosts = (
  grants: UsageGrant[],
  period: string,
  currentPeriod: string,
  now: number = Date.now()
): Boost[] => {
  if (period !== currentPeriod) return []
  return grants
    .filter(g => ANNOUNCED.has(g.kind))
    // ⚠ Compute is in cents and the notice prints whole cents: 0.4 of a cent left reads "$0.00
    // compute of extra allowance left", which is a spent grant.
    .filter(g => (g.axis === 'compute' ? Math.round(g.remaining) : g.remaining) > 0)
    .filter(g => !g.expiresAt || new Date(g.expiresAt).getTime() > now)
    .map(g => ({ axis: g.axis, amount: g.remaining, expiresAt: g.expiresAt, kind: g.kind }))
}

// soonestExpiry is the date the notice should name: the first one to go.
//
// ⚠ Naming the LAST one would promise the largest allowance for longer than it exists.
//
// ⚠⚠ A MIXED SET STILL NAMES THE FIRST DATE. An earlier draft returned null unless EVERY boost
// carried an expiry, and null is what makes the notice say "This extra allowance has no end date"
// — so one dated grant beside one undated one produced the largest over-promise the notice can
// make, in the one case it was written to prevent. Naming the soonest under-promises for the
// undated part, which is the safe direction and the one the caption already reads correctly.
//
// Null means NOTHING in the set expires, which is possible for a compensation granted without an
// end.
export const soonestExpiry = (boosts: Boost[]): string | null => {
  const dated = boosts.map(b => b.expiresAt).filter((d): d is string => !!d)
  if (dated.length === 0) return null
  return dated.reduce((a, b) => (new Date(a).getTime() <= new Date(b).getTime() ? a : b))
}

// The axis's own unit: bandwidth is bytes, compute is cents of retail compute. This is the same
// rule `formatAxisAmount` follows for the meters, restated here because a boost is not an axis row
// and must not borrow a formatter chosen by name.
const amountOf = (b: Boost): string =>
  b.axis === 'bandwidth' ? formatBytes(b.amount) : formatCents(b.amount)

// Only the two flows are announceable. A grant on `money` is a balance and belongs with the credit
// block, not in a sentence about allowances.
const AXIS_NOUN: Record<string, string> = {
  bandwidth: 'bandwidth',
  compute: 'compute',
}

// boostPhrase renders the set as one clause: "50 GB bandwidth and $5.00 compute".
//
// ⚠ The amounts are what is LEFT of each grant (`liveBoosts` reads `remaining`), so the sentence
// around this clause must say "left" and never "added" (app#446).
//
// ⚠ An empty string is the signal to render NOTHING. A grant on an axis this sentence cannot name
// must not produce a sentence with a gap in it, which is what a length check on the INPUT
// rather than on the named parts would do.
export const boostPhrase = (boosts: Boost[]): string => {
  const parts = boosts.filter(b => AXIS_NOUN[b.axis]).map(b => `${amountOf(b)} ${AXIS_NOUN[b.axis]}`)
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0]
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}
